// Helper function to get flag emoji from country code

import { WARNING_LEVEL_MAP } from '../constants';
import { PhoneNumberEntry, ProspectOption } from '../types';
// Country codes should be ISO 3166-1 alpha-2 format (e.g., 'US', 'IN', 'GB')
export const getCountryFlagEmoji = (countryCode: string): string => {
  // Map common country codes to flag emojis
  const countryCodeToFlag: Record<string, string> = {
    '+1': '🇺🇸', // United States
    '+91': '🇮🇳', // India
    '+44': '🇬🇧', // United Kingdom
    '+33': '🇫🇷', // France
    '+49': '🇩🇪', // Germany
    '+81': '🇯🇵', // Japan
    '+86': '🇨🇳', // China
    '+61': '🇦🇺', // Australia
    '+7': '🇷🇺', // Russia
    '+39': '🇮🇹', // Italy
    '+34': '🇪🇸', // Spain
    '+31': '🇳🇱', // Netherlands
    '+32': '🇧🇪', // Belgium
    '+41': '🇨🇭', // Switzerland
    '+46': '🇸🇪', // Sweden
    '+47': '🇳🇴', // Norway
    '+45': '🇩🇰', // Denmark
    '+353': '🇮🇪', // Ireland
    '+48': '🇵🇱', // Poland
    '+90': '🇹🇷', // Turkey
    '+55': '🇧🇷', // Brazil
    '+52': '🇲🇽', // Mexico
    '+27': '🇿🇦', // South Africa
    '+82': '🇰🇷', // South Korea
    '+65': '🇸🇬', // Singapore
    '+60': '🇲🇾', // Malaysia
    '+66': '🇹🇭', // Thailand
    '+84': '🇻🇳', // Vietnam
    '+62': '🇮🇩', // Indonesia
    '+971': '🇦🇪', // UAE
    '+966': '🇸🇦', // Saudi Arabia
    '+20': '🇪🇬', // Egypt
    '+212': '🇲🇦', // Morocco
    '+234': '🇳🇬', // Nigeria
    '+254': '🇰🇪', // Kenya
    '+64': '🇳🇿', // New Zealand
    '+358': '🇫🇮', // Finland
    '+420': '🇨🇿', // Czech Republic
    '+36': '🇭🇺', // Hungary
  };

  // Try direct match first
  if (countryCodeToFlag[countryCode]) {
    return countryCodeToFlag[countryCode];
  }

  // Try to extract country code and convert
  // For codes like "+1", we can derive from the code
  // Default to a globe or flag emoji
  return '🌐';
};

// Get country ISO code from phone code (for flag emoji)
export const getCountryISOCode = (phoneCode: string): string => {
  const phoneCodeToISO: Record<string, string> = {
    '+1': 'US',
    '+91': 'IN',
    '+44': 'GB',
    '+33': 'FR',
    '+49': 'DE',
    '+81': 'JP',
    '+86': 'CN',
    '+61': 'AU',
    '+7': 'RU',
    '+39': 'IT',
    '+34': 'ES',
    '+31': 'NL',
    '+32': 'BE',
    '+41': 'CH',
    '+46': 'SE',
    '+47': 'NO',
    '+45': 'DK',
    '+353': 'IE',
    '+48': 'PL',
    '+90': 'TR',
    '+55': 'BR',
    '+52': 'MX',
    '+27': 'ZA',
    '+82': 'KR',
    '+65': 'SG',
    '+60': 'MY',
    '+66': 'TH',
    '+84': 'VN',
    '+62': 'ID',
    '+971': 'AE',
    '+966': 'SA',
    '+20': 'EG',
    '+212': 'MA',
    '+234': 'NG',
    '+254': 'KE',
    '+64': 'NZ',
    '+358': 'FI',
    '+420': 'CZ',
    '+36': 'HU',
  };

  return phoneCodeToISO[phoneCode] || 'US';
};

export const countries = [
  { countryCode: '+1', countryName: 'United States' },
  { countryCode: '+91', countryName: 'India' },
  { countryCode: '+44', countryName: 'United Kingdom' },
  { countryCode: '+33', countryName: 'France' },
  { countryCode: '+49', countryName: 'Germany' },
  { countryCode: '+81', countryName: 'Japan' },
  { countryCode: '+86', countryName: 'China' },
  { countryCode: '+61', countryName: 'Australia' },
  { countryCode: '+7', countryName: 'Russia' },
  { countryCode: '+39', countryName: 'Italy' },
  { countryCode: '+34', countryName: 'Spain' },
  { countryCode: '+31', countryName: 'Netherlands' },
  { countryCode: '+32', countryName: 'Belgium' },
  { countryCode: '+41', countryName: 'Switzerland' },
  { countryCode: '+46', countryName: 'Sweden' },
  { countryCode: '+47', countryName: 'Norway' },
  { countryCode: '+45', countryName: 'Denmark' },
  { countryCode: '+353', countryName: 'Ireland' },
  { countryCode: '+48', countryName: 'Poland' },
  { countryCode: '+90', countryName: 'Turkey' },
  { countryCode: '+55', countryName: 'Brazil' },
  { countryCode: '+52', countryName: 'Mexico' },
  { countryCode: '+27', countryName: 'South Africa' },
  { countryCode: '+82', countryName: 'South Korea' },
  { countryCode: '+65', countryName: 'Singapore' },
  { countryCode: '+60', countryName: 'Malaysia' },
  { countryCode: '+66', countryName: 'Thailand' },
  { countryCode: '+84', countryName: 'Vietnam' },
  { countryCode: '+62', countryName: 'Indonesia' },
  { countryCode: '+971', countryName: 'UAE' },
  { countryCode: '+966', countryName: 'Saudi Arabia' },
  { countryCode: '+20', countryName: 'Egypt' },
  { countryCode: '+212', countryName: 'Morocco' },
  { countryCode: '+234', countryName: 'Nigeria' },
  { countryCode: '+254', countryName: 'Kenya' },
  { countryCode: '+64', countryName: 'New Zealand' },
  { countryCode: '+358', countryName: 'Finland' },
  { countryCode: '+420', countryName: 'Czech Republic' },
  { countryCode: '+36', countryName: 'Hungary' },
];

export const getInitials = (name: string): string => {
  if (!name?.trim()) {
    return '-';
  }
  if (name === 'Unknown Prospect') {
    return '-';
  }
  const words = name?.split(' ');
  if (words[0]?.length === 1) {
    return `${words[0]?.toUpperCase()}${words[0]?.toUpperCase()}`;
  }
  if (words?.length && words?.length >= 2) {
    const firstName = words?.[0]?.[0] ?? '';
    const lastName = words?.[1]?.[0] ?? '';
    return (firstName + lastName)?.toUpperCase() || '-';
  }
  return name?.substring(0, 2)?.toUpperCase() || '-';
};

// TODO: Remove this function and use the one from the backend
export const normalizePhoneNumbers = (
  raw: ProspectOption['phoneNumbers'],
): PhoneNumberEntry[] => {
  if (!raw?.length) return [];
  const labels = ['Work', 'Personal 1', 'Work', 'Personal 2', 'Mobile'];
  return raw?.map((item, i) => {
    if (typeof item === 'string') {
      return {
        number: item,
        label: labels[i] ?? `Number ${i + 1}`,
        isPrimary: i === 0,
      };
    }
    const obj = item as {
      phoneNumber?: string;
      number?: string;
      isPrimary?: boolean;
      phoneType?: string;
    };
    const number = obj?.phoneNumber ?? obj?.number ?? '';
    return {
      number,
      label: obj.phoneType,
      isPrimary: obj?.isPrimary ?? i === 0,
    };
  });
};

export const getPhoneCountAndFirst = (
  raw: ProspectOption['phoneNumbers'],
): { count: number; first: string } => {
  if (!raw?.length) return { count: 0, first: '' };
  const first = raw?.find((item) => item?.isPrimary)?.number;

  const firstNum = first || raw?.[0]?.number || '';
  return { count: raw.length, first: firstNum };
};

export const trimSpecialCharacterFromPhoneNumber = (number: string) => {
  const num = number?.replace(/[^0-9]/g, '');
  return num;
};

/** Format seconds as MM:SS for live call duration display */
export const formatCallDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

export const warningToLevel = (warningName: string): number =>
  WARNING_LEVEL_MAP[warningName] ?? 3;

/** Phone keypad layout: 1–9, then *, 0, # */
export const KEYPAD_KEYS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
] as const;

export const SANITIZE_IN_CALL_INPUT_REGEX = /[^\d#*]/g;
